<!--
  Portal action that allows elements to be rendered outside their normal DOM hierarchy.
  Useful for modals, dropdowns, tooltips etc that need to break out of their parent's overflow/z-index context.
  Features:
  - Can target any element or selector to portal into
  - Optional backdrop/overlay layer
  - Configurable z-index
  - Automatically handles cleanup on destroy
-->

<script context="module" lang="ts">
  import { tick } from "svelte";

  interface Options {
    target?: any;
    hasBackLayer?: boolean;
    zIndex?: string;
  }
  export function portal(
    el: HTMLElement,
    options: Options = { target: "body", hasBackLayer: false }
  ) {
    let targetEl;
    const backLayer = document.createElement("div");

    async function update(newTarget: string): Promise<void> {
      options.target = newTarget;
      if (typeof options.target === "string") {
        targetEl = document.querySelector(options.target);
        if (targetEl === null) {
          await tick();
          targetEl = document.querySelector(options.target);
        }
        if (targetEl === null) {
          throw new Error(`No element found matching css selector: "${options.target}"`);
        }
      } else if (options.target && options.target instanceof HTMLElement) {
        targetEl = options.target;
      } else {
        throw new TypeError(
          `Unknown portal options.target type: ${
            options.target === null ? "null" : typeof options.target
          }. Allowed types: string (CSS selector) or HTMLElement.`
        );
      }

      if (options.hasBackLayer) {
        backLayer.classList.add("portal", "fixed", "inset-0", options.zIndex ?? "z-modal");
        targetEl.appendChild(backLayer);
      }
      el.classList.add("portal", options.zIndex ?? "z-modal");
      targetEl.appendChild(el);

      el.hidden = false;
    }

    function destroy() {
      backLayer.remove();
      el.remove();
    }

    update(options.target);
    return {
      update,
      destroy,
    };
  }
</script>

<script>
  export let options = { target: "body", hasBackLayer: false };
</script>

<!-- the code below is an augmented version of https://github.com/romkor/svelte-portal -->

<div use:portal={options} hidden>
  <slot />
</div>
